import React, { useMemo } from 'react'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { Icon } from '../../atoms/Icon'
import type { LandSectionProps } from './LandSection.types'
import { LandMetrics } from './components/LandMetrics'
import { LandInfo } from './components/LandInfo'
import { LandZones } from './components/LandZones'
import { LandRestrictions } from './components/LandRestrictions'
import { LandOwners } from './components/LandOwners'
import { LandLinks } from './components/LandLinks'
import {
  LandSectionContainer,
  LoadingContainer,
  ErrorContainer,
  ErrorMessage,
  EmptyContainer,
  EmptyMessage,
  ContentGrid,
} from './LandSection.styles'

export const LandSection: React.FC<LandSectionProps> = ({
  land,
  loading,
  error,
  onRetry,
  onOwnerClick,
  className,
  t,
  tRestrictions,
}) => {
  // Calculate restriction counts
  const restrictionCounts = useMemo(() => {
    if (!land?.rdppfs || land.rdppfs.length === 0) {
      return {
        affected: 0,
        notAffected: 0,
        notAvailable: 0,
      }
    }

    // Every canonical reference is an intersection, including point/line records without area.
    const affected = land.rdppfs.length
    const notAffected = 0
    const notAvailable = 0

    return {
      affected,
      notAffected,
      notAvailable,
    }
  }, [land?.rdppfs])

  // Format plot number
  const formatPlotNumber = (land: any): string => {
    const municipality = land.municipality_name || ''
    const code = land.code_number || ''
    return municipality && code
      ? `${municipality} ${code}`
      : code || t('common.notAvailable')
  }

  // Loading state
  if (loading) {
    return (
      <LandSectionContainer className={className}>
        <LoadingContainer>
          <Loading size="large" />
          <p>{t('states.loading')}</p>
        </LoadingContainer>
      </LandSectionContainer>
    )
  }

  // Error state
  if (error) {
    return (
      <LandSectionContainer className={className}>
        <ErrorContainer>
          <Icon name="alert-circle" size="large" />
          <ErrorMessage>
            <h3>{t('states.error.title')}</h3>
            <p>{error}</p>
          </ErrorMessage>
          {onRetry && (
            <Button onClick={onRetry} variant="primary">
              <Icon name="refresh-cw" size="small" />
              {t('states.error.retry')}
            </Button>
          )}
        </ErrorContainer>
      </LandSectionContainer>
    )
  }

  // No land data
  if (!land) {
    return (
      <LandSectionContainer className={className}>
        <EmptyContainer>
          <Icon name="map" size="large" />
          <EmptyMessage>
            <h3>{t('states.empty.title')}</h3>
            <p>{t('states.empty.description')}</p>
          </EmptyMessage>
        </EmptyContainer>
      </LandSectionContainer>
    )
  }

  return (
    <LandSectionContainer className={className}>
      {/* Key Metrics */}
      <LandMetrics
        area={land.display_area ?? land.official_area ?? land.area ?? 0}
        buildingCount={land.building_nb || land.buildings?.length || 0}
        underExploitedScore={land.under_exploited_score || 0}
        t={t}
      />

      {/* Content Grid */}
      <ContentGrid>
        {/* Land Information */}
        <LandInfo
          egridNumber={land.egrid_nb || ''}
          plotNumber={formatPlotNumber(land)}
          type={land.ownership_type}
          area={land.display_area ?? land.official_area ?? land.area ?? 0}
          officialArea={land.official_area}
          isBuildable={!land.bare_land}
          hasBuildingArea={land.building_area || false}
          isDistinctPermanentRight={land.is_distinct_permanent_right || false}
          t={t}
        />

        {/* Assignment Zones */}
        {land.land_use_plans && land.land_use_plans.length > 0 && (
          <LandZones zones={land.land_use_plans} defaultExpanded={0} t={t} />
        )}

        {/* Restrictions */}
        {land.rdppfs && land.rdppfs.length > 0 && (
          <LandRestrictions
            restrictions={land.rdppfs}
            affectedCount={restrictionCounts.affected}
            notAffectedCount={restrictionCounts.notAffected}
            notAvailableCount={restrictionCounts.notAvailable}
            t={t}
            tRestrictions={tRestrictions}
          />
        )}

        {/* Owners */}
        {land.owners && land.owners.length > 0 && (
          <LandOwners
            owners={land.owners}
            ownerCount={land.owner_nb || land.owners.length}
            lastChangeDate={land.owner_last_change_date}
            ownershipType={land.ownership_type}
            onOwnerClick={onOwnerClick}
            t={t}
          />
        )}

        {/* Quick Links */}
        <LandLinks
          landId={land.id || land.land_id}
          latitude={
            typeof land.geo_center === 'object' &&
            'lat' in land.geo_center &&
            !Array.isArray(land.geo_center)
              ? land.geo_center.lat
              : undefined
          }
          longitude={
            typeof land.geo_center === 'object' &&
            'lon' in land.geo_center &&
            !Array.isArray(land.geo_center)
              ? land.geo_center.lon
              : undefined
          }
          t={t}
        />
      </ContentGrid>
    </LandSectionContainer>
  )
}
